import { useCallback, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Stack,
  Text,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  DefaultButton,
  Panel,
  PanelType,
  TextField,
  Toggle,
} from '@fluentui/react'
import { apiService } from '../services/apiService'
import { useApi, useMutation } from '../hooks/useApi'
import { useRoles } from '../hooks/useRoles'
import { StatusBadge, CredentialTypeBadge } from '../components/StatusBadge'
import type { Credential, CredentialRenewalFormData } from '../types'

export function CredentialDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const credentialId = parseInt(id || '0', 10)
  const { isAdmin } = useRoles()

  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false)
  const [formData, setFormData] = useState<CredentialRenewalFormData>({})

  const fetchCredential = useCallback(
    () => apiService.getCredential(credentialId),
    [credentialId]
  )

  const {
    data: credential,
    loading,
    error,
    refetch,
  } = useApi<Credential>(fetchCredential, [credentialId])

  const updateMutation = useMutation((data: CredentialRenewalFormData) =>
    apiService.updateCredentialRenewal(credentialId, data)
  )

  const renewMutation = useMutation(() =>
    apiService.markCredentialRenewed(credentialId)
  )

  const openEditPanel = () => {
    if (credential) {
      setFormData({
        renewalOwnerEmail: credential.renewalOwnerEmail || '',
        renewalNotes: credential.renewalNotes || '',
        renewalTicketUrl: credential.renewalTicketUrl || '',
        suppressNotifications: credential.suppressNotifications,
      })
    }
    setIsEditPanelOpen(true)
  }

  const handleSave = async () => {
    await updateMutation.mutate(formData)
    setIsEditPanelOpen(false)
    refetch()
  }

  const handleMarkRenewed = async () => {
    if (confirm('Mark this credential as renewed? This will reset the notification count.')) {
      await renewMutation.mutate({})
      refetch()
    }
  }

  if (loading) {
    return (
      <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { minHeight: 400 } }}>
        <Spinner size={SpinnerSize.large} label="Loading credential..." />
      </Stack>
    )
  }

  if (error || !credential) {
    return (
      <div className="page-container">
        <MessageBar messageBarType={MessageBarType.error}>
          {error || 'Credential not found'}
        </MessageBar>
        <DefaultButton
          text="Back to Credentials"
          onClick={() => navigate('/credentials')}
          styles={{ root: { marginTop: 16 } }}
        />
      </div>
    )
  }

  return (
    <div className="page-container">
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="start" style={{ marginBottom: 24 }}>
        <div>
          <DefaultButton
            iconProps={{ iconName: 'Back' }}
            text="Back"
            onClick={() => navigate('/credentials')}
            styles={{ root: { marginBottom: 12 } }}
          />
          <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
            <h1 className="page-title" style={{ margin: 0 }}>
              {credential.displayName || 'Unnamed Credential'}
            </h1>
            <CredentialTypeBadge type={credential.credentialType} />
            <StatusBadge expiresInDays={credential.expiresInDays} isExpired={credential.isExpired} />
          </Stack>
          <p className="page-description">
            Application:{' '}
            <span
              style={{ color: '#0078d4', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => navigate(`/applications/${credential.applicationId}`)}
            >
              {credential.applicationName}
            </span>
          </p>
        </div>
        {isAdmin && (
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <DefaultButton
              iconProps={{ iconName: 'Refresh' }}
              text="Mark as Renewed"
              title="Records that this credential was renewed and resets the notification count"
              onClick={handleMarkRenewed}
              disabled={renewMutation.loading}
            />
            <PrimaryButton
              iconProps={{ iconName: 'Edit' }}
              text="Edit Renewal Info"
              onClick={openEditPanel}
            />
          </Stack>
        )}
      </Stack>

      {/* Status Banner */}
      {credential.isExpired === true && (
        <MessageBar messageBarType={MessageBarType.severeWarning} styles={{ root: { marginBottom: 16 } }}>
          This credential has expired and may cause authentication failures!
        </MessageBar>
      )}
      {credential.isExpired === false && credential.expiresInDays <= 7 && (
        <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginBottom: 16 } }}>
          This credential expires in {credential.expiresInDays} days. Please renew it soon.
        </MessageBar>
      )}

      {/* Details Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div className="card">
          <h3 className="card-title">Credential Details</h3>
          <Stack tokens={{ childrenGap: 12 }}>
            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Type</Text>
              <Text block>{credential.credentialType}</Text>
            </div>
            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Key ID</Text>
              <Text block styles={{ root: { fontFamily: 'monospace', fontSize: 12 } }}>
                {credential.entraKeyId}
              </Text>
            </div>
            {credential.hint && (
              <div>
                <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Secret Hint</Text>
                <Text block>{credential.hint}</Text>
              </div>
            )}
            {credential.thumbprint && (
              <div>
                <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Thumbprint</Text>
                <Text block styles={{ root: { fontFamily: 'monospace', fontSize: 12 } }}>
                  {credential.thumbprint}
                </Text>
              </div>
            )}
            {credential.certificateSubject && (
              <div>
                <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Certificate Subject</Text>
                <Text block>{credential.certificateSubject}</Text>
              </div>
            )}
            {credential.certificateIssuer && (
              <div>
                <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Certificate Issuer</Text>
                <Text block>{credential.certificateIssuer}</Text>
              </div>
            )}
          </Stack>
        </div>

        <div className="card">
          <h3 className="card-title">Validity Period</h3>
          <Stack tokens={{ childrenGap: 12 }}>
            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Start Date</Text>
              <Text block>
                {credential.startDate ? new Date(credential.startDate).toLocaleDateString() : 'N/A'}
              </Text>
            </div>
            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>End Date</Text>
              <Text block styles={{ root: { fontWeight: 600, color: credential.isExpired ? '#a4262c' : undefined } }}>
                {new Date(credential.endDate).toLocaleDateString()}
              </Text>
            </div>
            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Days Remaining</Text>
              <Text block styles={{ root: { fontWeight: 600 } }}>
                {credential.isExpired ? 'EXPIRED' : `${credential.expiresInDays} days`}
              </Text>
            </div>
            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>First Discovered</Text>
              <Text block>{new Date(credential.firstDiscoveredAt).toLocaleString()}</Text>
            </div>
          </Stack>
        </div>

        <div className="card">
          <h3 className="card-title">Renewal Information</h3>
          <Stack tokens={{ childrenGap: 12 }}>
            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Application Owner</Text>
              <Text block>{credential.primaryOwnerEmail || 'Not assigned'}</Text>
            </div>
            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Renewal Notes</Text>
              <Text block>{credential.renewalNotes || 'No notes'}</Text>
            </div>
            {credential.renewalTicketUrl && (
              <div>
                <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Ticket URL</Text>
                <a href={credential.renewalTicketUrl} target="_blank" rel="noopener noreferrer">
                  View Ticket
                </a>
              </div>
            )}
            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Last Marked as Renewed</Text>
              <Text block>
                {credential.lastRenewedAt
                  ? `${new Date(credential.lastRenewedAt).toLocaleString()} by ${credential.lastRenewedBy}`
                  : 'Never'}
              </Text>
            </div>
          </Stack>
        </div>

        <div className="card">
          <h3 className="card-title">Notification Status</h3>
          <Stack tokens={{ childrenGap: 12 }}>
            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Notifications Sent</Text>
              <Text block>{credential.notificationCount}</Text>
            </div>
            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Last Notification</Text>
              <Text block>
                {credential.notificationSentAt
                  ? new Date(credential.notificationSentAt).toLocaleString()
                  : 'Never'}
              </Text>
            </div>
            <div>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Notifications</Text>
              <Text block styles={{ root: { color: credential.suppressNotifications ? '#a4262c' : '#107c10' } }}>
                {credential.suppressNotifications ? 'Suppressed' : 'Enabled'}
              </Text>
            </div>
          </Stack>
        </div>
      </div>

      {/* Federated Credential Details */}
      {credential.credentialType === 'FederatedCredential' && (
        <div className="card">
          <h3 className="card-title">Federated Identity Details</h3>
          <Stack tokens={{ childrenGap: 12 }}>
            {credential.federatedIssuer && (
              <div>
                <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Issuer</Text>
                <Text block styles={{ root: { fontFamily: 'monospace', fontSize: 12 } }}>
                  {credential.federatedIssuer}
                </Text>
              </div>
            )}
            {credential.federatedSubject && (
              <div>
                <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Subject</Text>
                <Text block styles={{ root: { fontFamily: 'monospace', fontSize: 12 } }}>
                  {credential.federatedSubject}
                </Text>
              </div>
            )}
            {credential.federatedAudiences && (
              <div>
                <Text variant="small" styles={{ root: { color: '#605e5c' } }}>Audiences</Text>
                <Text block styles={{ root: { fontFamily: 'monospace', fontSize: 12 } }}>
                  {credential.federatedAudiences}
                </Text>
              </div>
            )}
          </Stack>
        </div>
      )}

      {/* Edit Panel */}
      <Panel
        isOpen={isEditPanelOpen}
        onDismiss={() => setIsEditPanelOpen(false)}
        headerText="Edit Renewal Information"
        type={PanelType.medium}
        onRenderFooterContent={() => (
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <PrimaryButton
              text="Save"
              onClick={handleSave}
              disabled={updateMutation.loading}
            />
            <DefaultButton text="Cancel" onClick={() => setIsEditPanelOpen(false)} />
          </Stack>
        )}
        isFooterAtBottom
      >
        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { marginTop: 20 } }}>
          {updateMutation.error && (
            <MessageBar messageBarType={MessageBarType.error}>{updateMutation.error}</MessageBar>
          )}
          <TextField
            label="Renewal Notes"
            value={formData.renewalNotes}
            onChange={(_, val) => setFormData({ ...formData, renewalNotes: val })}
            multiline
            rows={4}
            placeholder="Add notes about renewal process, special instructions, etc."
          />
          <TextField
            label="Ticket URL"
            value={formData.renewalTicketUrl}
            onChange={(_, val) => setFormData({ ...formData, renewalTicketUrl: val })}
            placeholder="https://..."
          />
          <Toggle
            label="Suppress Notifications"
            checked={formData.suppressNotifications}
            onChange={(_, checked) => setFormData({ ...formData, suppressNotifications: checked })}
            onText="Notifications are suppressed"
            offText="Notifications are enabled"
          />
        </Stack>
      </Panel>
    </div>
  )
}
